import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService } from '@/api/services/client.service';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import type { TenderFilters, SubmitOfferRequest } from '@/api/types';

export const useTenders = (filters?: TenderFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.tenders.list(filters),
    queryFn: () => clientService.getTenders(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options?.enabled !== false, // Only call if enabled is true (client only)
    retry: 1, // Retry once on failure
    retryDelay: 1000, // Wait 1 second before retry
  });
};

export const useTender = (id: number) => {
  return useQuery({
    queryKey: queryKeys.tenders.detail(id),
    queryFn: () => clientService.getTenderDetails(id),
    enabled: !!id,
  });
};

export const useSubmitOffer = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: SubmitOfferRequest) => clientService.submitOffer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.offers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.client() });
      toast({
        title: t('tenders.notifications.offerSubmitted') || 'تم تقديم العرض بنجاح',
        description: t('tenders.notifications.offerSubmittedDesc') || 'سيتم مراجعة عرضك والتواصل معك قريباً',
      });
    },
    onError: (error: any) => {
      const responseMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      toast({
        title: t('tenders.notifications.offerFailed') || 'فشل تقديم العرض',
        description: responseMessage || (t('tenders.notifications.offerFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};
